import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { formatPct } from '../../utils/formatPercentage';
import { commitmentColor } from '../../utils/metricColors';
import { useRoute, useNavigation, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle, G } from 'react-native-svg';
import { initiativesApi, kpisApi, activitiesApi, initiativeCommentsApi, InitiativeComment } from '../../services/apiService';
import { Initiative, KPIList, ActivityList } from '../../types';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import TargetAchievementCard from '../../components/TargetAchievementCard';
import { COLORS, FONTS, LABELS } from '../../constants/config';
import {
  LoadingScreen,
  EmptyState,
  PageContainer,
  Navbar,
  CommentSection,
  CommentData,
  getInitialsFromUserType,
  getAvatarColorFromUserType,
  UserType,
  Breadcrumb,
  EvidenceTable,
} from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { getCachedData, setCachedData } from '../../utils/dataCache';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { IconButton } from '@design/components/IconButton';
import { PageHeader } from '@design/components/PageHeader';
type RouteProps = RouteProp<MainStackParamList, 'InitiativeDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EyeIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M2.06153 12.3474C1.97819 12.1229 1.97819 11.8759 2.06153 11.6514C2.87323 9.68324 4.25104 8.00042 6.0203 6.81628C7.78955 5.63214 9.87057 5 11.9995 5C14.1285 5 16.2095 5.63214 17.9788 6.81628C19.748 8.00042 21.1258 9.68324 21.9375 11.6514C22.0209 11.8759 22.0209 12.1229 21.9375 12.3474C21.1258 14.3155 19.748 15.9983 17.9788 17.1825C16.2095 18.3666 14.1285 18.9988 11.9995 18.9988C9.87057 18.9988 7.78955 18.3666 6.0203 17.1825C4.25104 15.9983 2.87323 14.3155 2.06153 12.3474Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 20, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 20, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V12" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.3751 2.62523C18.7729 2.2274 19.3125 2.00391 19.8751 2.00391C20.4377 2.00391 20.9773 2.2274 21.3751 2.62523C21.7729 3.02305 21.9964 3.56262 21.9964 4.12523C21.9964 4.68784 21.7729 5.2274 21.3751 5.62523L12.3621 14.6392C12.1246 14.8765 11.8313 15.0501 11.5091 15.1442L8.63609 15.9842C8.55005 16.0093 8.45883 16.0108 8.372 15.9886C8.28517 15.9663 8.20592 15.9212 8.14254 15.8578C8.07916 15.7944 8.03398 15.7151 8.01174 15.6283C7.98949 15.5415 7.991 15.4503 8.01609 15.3642L8.85609 12.4912C8.95062 12.1693 9.12463 11.8763 9.36209 11.6392L18.3751 2.62523Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ArrowRightIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M5 12H19M19 12L12 5M19 12L12 19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const SendIcon = ({ size = 16, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M22 2L11 13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M22 2L15 22L11 13L2 9L22 2Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Circular Gauge Component
// ============================================================

interface CircularGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
}

const CircularGauge = ({
  percentage,
  size = 120,
  strokeWidth = 12,
  color = '#2CA02C',
  backgroundColor = tokens.colors.raw.gray["200"]
}: CircularGaugeProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={color}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={{ fontSize: size > 100 ? 24 : 16, fontFamily: FONTS.numbers, fontWeight: '500', color: tokens.colors.semantic.general.foreground }}>
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Donut Chart Component for Status Distribution - Figma Design
// ============================================================

interface PieChartProps {
  excellent: number; // Green
  good: number; // Orange
  needsImprovement: number; // Red
  size?: number;
}

const PieChart = ({ excellent, good, needsImprovement, size = 120 }: PieChartProps) => {
  const total = excellent + good + needsImprovement || 1;
  const center = size / 2;
  const outerRadius = size / 2 - 5;
  const innerRadius = outerRadius * 0.6; // Donut hole
  const strokeWidth = outerRadius - innerRadius;
  const radius = (outerRadius + innerRadius) / 2;
  const circumference = 2 * Math.PI * radius;

  // Calculate stroke dash arrays for each segment
  const excellentPercent = excellent / total;
  const goodPercent = good / total;
  const needsImprovementPercent = needsImprovement / total;

  const excellentDash = circumference * excellentPercent;
  const goodDash = circumference * goodPercent;
  const needsImprovementDash = circumference * needsImprovementPercent;

  // Calculate rotation offsets (start from top, go clockwise)
  const excellentRotation = -90;
  const goodRotation = excellentRotation + (excellentPercent * 360);
  const needsImprovementRotation = goodRotation + (goodPercent * 360);

  return (
    <Svg width={size} height={size}>
      {/* Excellent - Green */}
      {excellent > 0 && (
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={tokens.colors.raw.green["500"]}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${excellentDash} ${circumference}`}
          strokeDashoffset={0}
          rotation={excellentRotation}
          origin={`${center}, ${center}`}
        />
      )}
      {/* Good - Orange */}
      {good > 0 && (
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={tokens.colors.raw.orange["500"]}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${goodDash} ${circumference}`}
          strokeDashoffset={0}
          rotation={goodRotation}
          origin={`${center}, ${center}`}
        />
      )}
      {/* Needs Improvement - Red */}
      {needsImprovement > 0 && (
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={tokens.colors.raw.red["500"]}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${needsImprovementDash} ${circumference}`}
          strokeDashoffset={0}
          rotation={needsImprovementRotation}
          origin={`${center}, ${center}`}
        />
      )}
    </Svg>
  );
};

// ============================================================
// KPI Card Component
// ============================================================

// ============================================================
// Activity Card Component
// ============================================================

interface ActivityCardProps {
  activity: ActivityList;
  onViewDetails?: (id: number) => void;
  cardWidth?: number;
  t: (key: string) => string;
  translate?: (text: string | undefined | null) => string;
}

const ActivityCard = ({ activity, onViewDetails, cardWidth, t, translate }: ActivityCardProps) => {
  const { colors, isDark } = useTheme();
  const displayName = translate ? translate(activity.name) : activity.name;

  // Commitment rate; null (nothing due yet) → "--" and an empty bar
  const commitment = activity.commitment_rate ?? null;
  const percentage = commitment == null ? 0 : parseFloat(Number(commitment).toFixed(2));
  const statusColor = commitmentColor(commitment);
  const displayValue = commitment == null ? '--' : `% ${formatPct(percentage)}`;

  return (
    <View style={[styles.activityCard, cardWidth && { width: cardWidth }, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      <Text style={[styles.activityCardTitle, { color: colors.textPrimary }]}>{displayName}</Text>

      <View style={styles.kpiValueRow}>
        <View style={[styles.kpiValueBadge, { backgroundColor: colors.backgroundAlt }]}>
          <Text style={[styles.kpiValue, { color: colors.textPrimary }]}>{displayValue}</Text>
        </View>

        <View style={styles.kpiProgressContainer}>
          <View style={[styles.kpiProgressTrack, { backgroundColor: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"] }]}>
            <View style={[styles.kpiProgressFill, { width: `${percentage}%`, backgroundColor: statusColor }]} />
            <View style={[styles.kpiProgressDot, { backgroundColor: statusColor, end: `${percentage}%`, transform: [{ translateX: 7 }] }]} />
          </View>
        </View>
      </View>

      <TouchableOpacity
        style={styles.kpiDetailsLink}
        onPress={() => activity.id && onViewDetails?.(activity.id)}
        disabled={!activity.id}
      >
        <ChevronLeftIcon size={16} color={activity.id ? colors.textPrimary : colors.textMuted} />
        <Text style={[styles.kpiDetailsText, { color: activity.id ? colors.textPrimary : colors.textMuted }]}>{t('common.viewDetails')}</Text>
      </TouchableOpacity>
    </View>
  );
};


// ============================================================
// Main Screen
// ============================================================

export default function InitiativeDetailScreen() {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavigationProp>();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, isViewOnly, canComment } = useRoleNavigation();
  const { id } = route.params;
  const { isMobile, isTablet, isDesktop, isSmallMobile, horizontalCardWidth, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
  };

  // Check if user is strategy_team (they can't add activities)
  const isStrategyTeam = user?.profile?.user_type === 'strategy_team' || user?.profile?.user_type === 'superuser';

  // Stack on tablet + mobile (< 1024px) — tablet too narrow for desktop grid.
  const isSmallScreen = !isDesktop;

  const [initiative, setInitiative] = useState<Initiative | null>(cachedInit);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [activities, setActivities] = useState<ActivityList[]>([]);
  const [apiComments, setApiComments] = useState<InitiativeComment[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  const canApproveDeletion = ['org_executive', 'strategy_team', 'superuser'].includes(user?.profile?.user_type || '');
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const canViewAllEvidence = ['strategy_team', 'executive', 'performance_evaluator', 'evaluation_approver', 'superuser'].includes(user?.profile?.user_type || '');

  // Cached loading state - shows cached data instantly on revisit
  const cachedInit = useRef(getCachedData<any>(`initiative-${id}`)).current;
  const [loading, setLoading] = useState(!cachedInit);

  // Comments state - unified with GoalDetailScreen pattern
  const [showAddComment, setShowAddComment] = useState(false);
  const [editingComment, setEditingComment] = useState<CommentData | null>(null);


  // Convert API comment to CommentData format for display (same as GoalDetailScreen)
  const convertApiComment = (apiComment: InitiativeComment): CommentData => {
    // Get the actual author name from author_details
    const fullName = apiComment.author_details?.full_name || apiComment.author_details?.username || t('common.user');
    const nameParts = fullName.split(' ').filter(Boolean);
    const authorName = nameParts[0] || t('common.user'); // First name only

    // Generate initials from the actual name
    const initials = nameParts.length >= 2
      ? `${nameParts[0][0]} ${nameParts[1][0]}`
      : fullName.slice(0, 2);

    const isOwner = user?.id === apiComment.author;
    const userType = (apiComment.author_type || 'org_employee') as UserType;
    const avatarColor = getAvatarColorFromUserType(userType);

    return {
      id: apiComment.id,
      name: authorName,
      initials,
      date: new Date(apiComment.created_at).toLocaleDateString('ar-SA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      text: apiComment.content,
      avatarColor,
      showMenu: isOwner, // Only show menu for own comments
      isEdited: apiComment.is_edited,
      originalText: apiComment.original_content,
      userType,
    };
  };

  // Convert API comments to display format
  const comments = apiComments.map(convertApiComment);

  const fetchComments = useCallback(async () => {
    try {
      const commentsData = await initiativeCommentsApi.getForInitiative(id);
      setApiComments(commentsData);
    } catch {
      setApiComments([]);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const data = await initiativesApi.get(id);

      setInitiative(data);
      setCachedData(`initiative-${id}`, data);

      // Use KPIs and activities from initiative response (already filtered consistently with kpi_distribution)
      // This ensures the KPIs/activities lists match the distribution data
      setKpis(data.kpis || []);
      setActivities(data.activities || []);

      // Fetch comments for this initiative
      await fetchComments();
    } catch (err: any) {
      setError(err.message || t('messages.error.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, fetchComments]);

  // Auto-refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Auto-refresh data when screen comes into focus (e.g., after adding activity/KPI)
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleKPIViewDetails = (kpiId: number) => {
    navigation.navigate('KPIDetail', { id: kpiId });
  };

  const handleActivityViewDetails = (activityId: number) => {
    navigation.navigate('ActivityDetail', { id: activityId });
  };

  // Comment handlers - unified with GoalDetailScreen pattern
  const handleAddComment = async (text: string) => {
    try {
      // Determine author_type based on user profile
      const authorType = user?.profile?.user_type === 'strategy_team' ? 'strategy_team' : 'org_employee';

      await initiativeCommentsApi.create({
        initiative: id,
        content: text,
        comment_type: 'note',
        author_type: authorType,
      });
      fetchComments();
      setShowAddComment(false);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleEditComment = (comment: CommentData) => {
    setEditingComment(comment);
    setShowAddComment(true);
  };

  const handleUpdateComment = async (text: string) => {
    if (editingComment) {
      try {
        await initiativeCommentsApi.update(editingComment.id, { content: text });
        fetchComments();
      } catch (error) {
        console.error('Error updating comment:', error);
      } finally {
        setEditingComment(null);
        setShowAddComment(false);
      }
    }
  };

  const handleDeleteInitiative = () => { setShowDeletionImpact(true); };
  const confirmDeleteInitiative = async () => {
    try {
      setIsDeleting(true);
      const response = await initiativesApi.delete(id);
      if ((response as any)?.status === 202) { showSuccess(t('deletion.requestSent')); }
      else { showSuccess(t('initiatives.deleteSuccess')); }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) { showSuccess(t('deletion.requestSent')); navigation.goBack(); }
      else if (error.response?.status === 409) { showError(t('deletion.alreadyPending')); }
      else { showError(t('messages.deleteFailed')); }
    } finally { setIsDeleting(false); setShowDeletionImpact(false); }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const response = await initiativeCommentsApi.delete(commentId);
      // Check if it was a delete request (org_employee) or direct delete
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleCancelComment = () => {
    setShowAddComment(false);
    setEditingComment(null);
  };

  const handleCommentSubmit = (text: string) => {
    if (editingComment) {
      handleUpdateComment(text);
    } else {
      handleAddComment(text);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !initiative) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={LABELS.error}
        message={error || t('initiatives.notFound')}
        actionLabel={LABELS.refresh}
        onAction={fetchData}
      />
    );
  }

  // Helper function for achievement-based colors
  const getAchievementColor = (percentage: number): string => {
    if (percentage >= 100) return tokens.colors.raw.green["500"];  // Green - Excellent
    if (percentage >= 80) return tokens.colors.raw.orange["500"];   // Orange - Good
    return tokens.colors.raw.red["500"];                          // Red - Needs Improvement
  };

  // Use kpi_distribution from API response instead of local calculation
  const distribution = initiative?.kpi_distribution ?? { excellent: 0, good: 0, needs_improvement: 0, total: 0 };
  const totalKpis = distribution.total ?? kpis.length;
  const excellentPercent = distribution.excellent ?? 0;
  const goodPercent = distribution.good ?? 0;
  const needsImprovementPercent = distribution.needs_improvement ?? 0;

  // commitment_rate is null when no measurement periods are due yet → "--"
  const avgPerformance = initiative?.commitment_rate ?? null;

  // deviation_percentage is null when no measurement periods are due yet
  const deviation = initiative?.deviation_percentage ?? null;

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('navigation.initiatives'), route: 'Initiatives' },
            { label: translate(initiative.title) },
          ]}
        />

        <PageHeader
          title={translate(initiative.title)}
          description={translate(initiative.description) || t('common.noDescription')}
          badge={
            initiative.approval_status === 'pending' ? (
              <View style={styles.pendingStatusBadge}>
                <Text style={styles.pendingStatusText}>{t('initiatives.pendingApproval')}</Text>
              </View>
            ) : initiative.approval_status === 'draft' ? (
              <View style={styles.draftStatusBadge}>
                <Text style={styles.draftStatusText}>{t('goals.draft')}</Text>
              </View>
            ) : initiative.approval_status === 'rejected' ? (
              <View style={styles.rejectedStatusBadge}>
                <Text style={styles.rejectedStatusText}>{t('goals.rejected')}</Text>
              </View>
            ) : initiative.approval_status === 'approved' ? (
              <View style={styles.approvedStatusBadge}>
                <Text style={styles.approvedStatusText}>{t('goals.approved')}</Text>
              </View>
            ) : undefined
          }
          actions={
            !isViewOnly ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute["2"] }}>
                <IconButton
                  variant="destructive"
                  onPress={handleDeleteInitiative}
                  icon={<TrashIcon />}
                  accessibilityLabel={t('common.delete')}
                />
                <IconButton
                  variant="secondary"
                  onPress={() => navigation.navigate('AddInitiative', { initiativeId: id })}
                  icon={<EditIcon />}
                  accessibilityLabel={t('common.edit')}
                />
              </View>
            ) : undefined
          }
          contentTrailing={initiative.department_name ? (
            <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', gap: tokens.spacing.absolute["2"], flexWrap: 'wrap' }}>
              <View style={styles.organizationBadge}>
                <Text style={styles.organizationBadgeText}>{translateOrg(initiative.department_name)}</Text>
              </View>
            </View>
          ) : undefined}
        />

        {/* Pending Deletion Banner */}
        {(initiative as any).pending_deletion_info && (
          <DeletionApprovalBanner
            deletionInfo={(initiative as any).pending_deletion_info}
            canApprove={canApproveDeletion}
            canCancel={isOrgEmployee && (initiative as any).pending_deletion_info.requested_by_id === user?.id}
            onApproved={() => { showSuccess(t('deletion.approved')); navigation.goBack(); }}
            onRejected={() => { showSuccess(t('deletion.rejected')); fetchData(); }}
            onCancelled={() => { showSuccess(t('deletion.requestCancelled')); fetchData(); }}
            onViewImpact={() => setShowDeletionImpact(true)}
          />
        )}

        {/* Info Cards Row - Figma Design */}
        <View style={[
          styles.infoCardsRow,
          { flexDirection: 'row-reverse' },
          isMobile && { flexWrap: 'wrap', gap: tokens.spacing.absolute["3"] }
        ]}>
          {/* Strategic Goal */}
          <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }, isMobile && { minWidth: '45%' }]}>
            <Text style={[styles.infoCardLabel, { color: colors.textPrimary }]}>{t('initiatives.strategicGoal')}</Text>
            <Text style={[styles.infoCardValue, { color: colors.textMuted }]}>
              {translate(initiative.strategic_goal_title) || '-'}
            </Text>
          </View>

          {/* Implementation Responsibility */}
          <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }, isMobile && { minWidth: '45%' }]}>
            <Text style={[styles.infoCardLabel, { color: colors.textPrimary }]}>{t('initiatives.implementationResponsibility')}</Text>
            <Text style={[styles.infoCardValue, { color: colors.textMuted }]}>
              {initiative.responsible_unit_names?.length > 0
                ? initiative.responsible_unit_names.map(name => translate(name)).join('\n')
                : '-'}
            </Text>
          </View>

          {/* Start and End Date */}
          <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }, isMobile && { minWidth: '45%' }]}>
            <Text style={[styles.infoCardLabel, { color: colors.textPrimary }]}>{t('initiatives.startEndDate')}</Text>
            <Text style={[styles.infoCardValue, { color: colors.textMuted }]}>
              {initiative.start_date && initiative.end_date
                ? `${t('common.from')} ${new Date(initiative.start_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })} ${t('common.to')} ${new Date(initiative.end_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : '-'}
            </Text>
          </View>

          {/* Resources */}
          <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }, isMobile && { minWidth: '45%' }]}>
            <Text style={[styles.infoCardLabel, { color: colors.textPrimary }]}>{t('initiatives.resources')}</Text>
            <Text style={[styles.infoCardValue, { color: colors.textMuted }]}>
              {initiative.resources_description || '-'}
            </Text>
          </View>
        </View>

        {/* Gauge Cards Row - Figma Design */}
        <View style={[
          styles.gaugeCardsRow,
          !isDesktop && { flexDirection: 'column', gap: tokens.spacing.absolute["4"] }
        ]}>
          {/* KPI Status Distribution - Fixed Width 538px */}
          <View style={[
            styles.statusDistributionCard,
            { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
            !isDesktop && { maxWidth: '100%' },
            isMobile && { padding: tokens.spacing.absolute["4"] }
          ]}>
            <Text style={[styles.gaugeCardTitleRight, { color: themeColors.textPrimary }]}>{t('kpis.statusDistribution')}</Text>
            <View style={[
              styles.statusDistributionContent,
              isMobile && { flexDirection: 'column', alignItems: 'center', gap: tokens.spacing.absolute["4"] }
            ]}>
              {/* Pie Chart on Left */}
              <View style={styles.donutChartWrapper}>
                <PieChart
                  excellent={excellentPercent}
                  good={goodPercent}
                  needsImprovement={needsImprovementPercent}
                  size={isMobile ? 100 : 120}
                />
              </View>
              {/* Legend on Right */}
              <View style={[
                styles.statusLegend,
                isMobile && { flexDirection: 'row', gap: tokens.spacing.absolute["4"], flexWrap: 'wrap', justifyContent: 'center' }
              ]}>
                <View style={styles.statusLegendItem}>
                  <Text style={[styles.statusLegendPercent, { color: themeColors.textPrimary }]}>% {excellentPercent}</Text>
                  <View style={styles.statusLegendLabelRow}>
                    <Text style={[styles.statusLegendLabel, { color: themeColors.textMuted }]}>{t('status.excellent')}</Text>
                    <View style={[styles.statusLegendBar, { backgroundColor: tokens.colors.raw.green["500"] }]} />
                  </View>
                </View>
                <View style={styles.statusLegendItem}>
                  <Text style={[styles.statusLegendPercent, { color: themeColors.textPrimary }]}>% {goodPercent}</Text>
                  <View style={styles.statusLegendLabelRow}>
                    <Text style={[styles.statusLegendLabel, { color: themeColors.textMuted }]}>{t('status.good')}</Text>
                    <View style={[styles.statusLegendBar, { backgroundColor: tokens.colors.raw.orange["500"] }]} />
                  </View>
                </View>
                <View style={styles.statusLegendItem}>
                  <Text style={[styles.statusLegendPercent, { color: themeColors.textPrimary }]}>% {needsImprovementPercent}</Text>
                  <View style={styles.statusLegendLabelRow}>
                    <Text style={[styles.statusLegendLabel, { color: themeColors.textMuted }]}>{t('status.needsImprovement')}</Text>
                    <View style={[styles.statusLegendBar, { backgroundColor: tokens.colors.raw.red["500"] }]} />
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Right Cards Container - Flex 1 */}
          <View style={[
            styles.gaugeCardsRight,
            !isDesktop && { flexDirection: 'column', gap: tokens.spacing.absolute["4"], flex: 0 }
          ]}>
            {/* Deviation Rate */}
            <View style={[
              styles.gaugeCardFlex,
              { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
              !isDesktop && { flex: 0, width: '100%', minWidth: 'auto', minHeight: 160 }
            ]}>
              <Text style={[styles.gaugeCardTitleRight, { color: themeColors.textPrimary }]}>{t('initiatives.deviationPercentage')}</Text>
              {deviation != null ? (
                <View style={styles.gaugeContainer}>
                  <CircularGauge
                    percentage={deviation}
                    size={isMobile ? 100 : 120}
                    strokeWidth={isMobile ? 10 : 12}
                    color="#FF7F0E"
                    backgroundColor={isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"]}
                  />
                </View>
              ) : (
                <View style={[styles.gaugeContainer, { justifyContent: 'center', alignItems: 'center', minHeight: isMobile ? 100 : 120 }]}>
                  <Text style={{ fontSize: 28, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
            </View>

            {/* Performance KPIs */}
            <View style={[
              styles.gaugeCardFlex,
              { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
              !isDesktop && { flex: 0, width: '100%', minWidth: 'auto', minHeight: 160 }
            ]}>
              <Text style={[styles.gaugeCardTitleRight, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
              {avgPerformance != null ? (
                <View style={styles.gaugeContainer}>
                  <CircularGauge
                    percentage={avgPerformance}
                    size={isMobile ? 100 : 120}
                    strokeWidth={isMobile ? 10 : 12}
                    color={commitmentColor(avgPerformance)}
                    backgroundColor={isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"]}
                  />
                </View>
              ) : (
                <View style={[styles.gaugeContainer, { justifyContent: 'center', alignItems: 'center', minHeight: isMobile ? 100 : 120 }]}>
                  <Text style={{ fontSize: 28, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Bottom Section - Figma Design (3-column grid) */}
        <View style={[
          styles.bottomSectionGrid,
          !isDesktop && { flexDirection: 'column', gap: tokens.spacing.absolute["4"] }
        ]}>
          {/* Right Column: Initiative Links (spans 2 columns) */}
          <View style={[
            styles.linksColumnFigma,
            !isDesktop && { flex: 0, width: '100%' }
          ]}>
            <View style={[styles.linksCardContainer, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              {/* Card Header */}
              <Text style={[styles.linksCardTitle, { color: themeColors.textPrimary }]}>{t('initiatives.initiativeLinks')}</Text>

              {/* Performance KPIs Section */}
              <View style={[styles.linksSectionGray, { backgroundColor: themeColors.backgroundAlt }]}>
                <View style={styles.linksSectionHeaderRow}>
                  {/* Add KPI Button - Hidden for strategy_team and view-only users */}
                  {!isStrategyTeam && !isViewOnly && (
                    <Button
                      variant="secondary"
                      onPress={() => navigation.navigate('AddInitiative', { initiativeId: id, scrollToKpi: true })}
                      trailingIcon={<PlusIcon size={16} color={themeColors.iconDefault} />}
                    >
                      {t('kpis.addIndicator')}
                    </Button>
                  )}
                  <Text style={[styles.linksSectionTitleText, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
                </View>

                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.horizontalCardsRow}
                    style={styles.horizontalScrollContainer}
                  >
                    {kpis.length > 0 ? (
                      kpis.map((kpi) => {
                        const achievement = parseFloat(String(kpi.achievement_percentage ?? '0')) || 0;
                        // Color based on achievement: green >= 80%, yellow >= 50%, red < 50%
                        // (same thresholds as Process/Service/UnitGoal detail KPI cards)
                        const progressColor =
                          achievement >= 80
                            ? '#2CA02C'
                            : achievement >= 50
                              ? tokens.colors.raw.yellow["500"]
                              : tokens.colors.raw.red["500"];

                        return (
                          <View key={kpi.id} style={[styles.rtlCard, { width: horizontalCardWidth }]}>
                            <TargetAchievementCard
                              title={translate(kpi.name)}
                              achieved={kpi.latest_value || '0'}
                              target={kpi.target_value}
                              unit={kpi.unit === 'percentage' ? '%' : translate(kpi.unit === 'count' ? 'عدد' : kpi.unit)}
                              achievedColor={progressColor}
                              percentage={achievement}
                              onViewDetails={() => handleKPIViewDetails(kpi.id)}
                            />
                          </View>
                        );
                      })
                    ) : (
                      <View style={[styles.emptyStateContainer, styles.rtlCard]}>
                        <Text style={styles.emptyStateText}>{t('initiatives.noLinkedKPIs')}</Text>
                      </View>
                    )}
                  </ScrollView>
                </View>
              </View>

              {/* Activities Section */}
              <View style={[styles.linksSectionGray, { backgroundColor: themeColors.backgroundAlt }]}>
                <View style={styles.linksSectionHeaderRow}>
                  {/* Add Activity Button - Hidden for strategy_team and view-only users */}
                  {!isStrategyTeam && !isViewOnly && (
                    <Button
                      variant="secondary"
                      onPress={() => navigation.navigate('AddActivity', { initiativeId: id })}
                      trailingIcon={<PlusIcon size={16} color={themeColors.iconDefault} />}
                    >
                      {t('initiatives.addActivity')}
                    </Button>
                  )}
                  <Text style={[styles.linksSectionTitleText, { color: themeColors.textPrimary }]}>{t('initiatives.activities')}</Text>
                </View>

                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.horizontalCardsRow}
                    style={styles.horizontalScrollContainer}
                  >
                    {activities.length > 0 ? (
                      activities.map((activity) => (
                        <View key={activity.id} style={styles.rtlCard}>
                          <ActivityCard
                            activity={activity}
                            cardWidth={horizontalCardWidth}
                            onViewDetails={handleActivityViewDetails}
                            t={t}
                            translate={translate}
                          />
                        </View>
                      ))
                    ) : (
                      <View style={[styles.emptyStateContainer, styles.rtlCard]}>
                        <Text style={styles.emptyStateText}>{t('initiatives.noLinkedActivities')}</Text>
                      </View>
                    )}
                  </ScrollView>
                </View>
              </View>
            </View>
          </View>

          {/* Left Column: Comments - Unified with GoalDetailScreen */}
          <View style={[
            styles.commentsColumn,
            !isDesktop && { flex: 0, width: '100%' }
          ]}>
            <CommentSection
              title={t('comments.title')}
              comments={comments}
              showAddComment={showAddComment}
              editingComment={editingComment}
              hideAddButton={!canComment}
              onAddClick={() => setShowAddComment(true)}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
              onSubmitComment={handleCommentSubmit}
              onCancelComment={handleCancelComment}
            />
          </View>
        </View>
        <EvidenceTable
          relatedType="initiative"
          filterRelatedId={id}
          title={t('evidence.sectionTitle')}
          canManage={canManageEvidence}
          showOrgColumn={canViewAllEvidence}
          onAddPress={() => navigation.navigate('AddEvidence', {
            relatedType: 'initiative', relatedId: id, contextLabel: t('initiatives.title'),
          })}
          onEditPress={(ev) => navigation.navigate('AddEvidence', {
            evidenceId: ev.id, relatedType: ev.related_type, relatedId: ev.related_id, contextLabel: t('initiatives.title'),
          })}
        />
      </ScrollView>

      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDeleteInitiative}
        entityName={initiative?.title || ''}
        fetchImpact={async () => { const r = await initiativesApi.deletionImpact(id); return r.data; }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.absolute["5"],
  },
  contentSmall: {
    padding: tokens.spacing.absolute["3"],
  },
  // Breadcrumb
  breadcrumbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbRowSmall: {
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["1"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbSeparator: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbCurrent: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Pending Status Badge
  pendingStatusBadge: {
    backgroundColor: '#9467BD',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  pendingStatusText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  draftStatusBadge: {
    backgroundColor: tokens.colors.raw.gray["500"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  draftStatusText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  rejectedStatusBadge: {
    backgroundColor: tokens.colors.raw.red["500"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  rejectedStatusText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  approvedStatusBadge: {
    backgroundColor: tokens.colors.raw.green["500"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  approvedStatusText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  // Organization Badge
  organizationBadge: {
    backgroundColor: tokens.colors.raw.amber["100"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-4"],
    alignSelf: 'flex-end',
  },
  organizationBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: '#92400E',
  },
  // Info Cards Row - Figma Design
  infoCardsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["5"],
  },
  infoCardsRowSmall: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["3"],
  },
  infoCard: {
    flex: 1,
    minWidth: 140,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    alignItems: 'flex-end',
    gap: tokens.spacing.absolute["1"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  infoCardLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  infoCardValue: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 14,
  },
  // Gauge Cards Row - Figma Design
  gaugeCardsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["5"],
  },
  gaugeCardsRowSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  // Status Distribution Card - Fixed Width
  statusDistributionCard: {
    width: '100%',
    maxWidth: 538,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["5"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  statusDistributionCardSmall: {
    maxWidth: '100%',
    padding: tokens.spacing.absolute["4"],
  },
  statusDistributionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  donutChartWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Status Legend
  statusLegend: {
    gap: tokens.spacing.absolute["6"],
  },
  statusLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  statusLegendPercent: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    color: '#303030',
    textAlign: getTextAlign(isRTL),
  },
  statusLegendLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1"],
  },
  statusLegendLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    width: 72,
    textAlign: getTextAlign(isRTL),
  },
  statusLegendBar: {
    width: 8,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-2"],
  },
  // Right Cards Container
  gaugeCardsRight: {
    flex: 1,
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
  },
  gaugeCardsRightSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  gaugeCardFlex: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["3"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    minWidth: 200,
  },
  gaugeCardFlexSmall: {
    flex: 0,
    width: '100%',
    minWidth: 'auto',
  },
  gaugeCardTitleRight: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    width: '100%',
    lineHeight: 24,
  },
  gaugeContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: tokens.spacing.absolute["2"],
  },
  // Bottom Section - Figma Design (3-column grid layout)
  bottomSectionGrid: {
    flexDirection: 'row-reverse', // RTL: Comments on left, Links on right
    gap: tokens.spacing.absolute["5"],
  },
  bottomSectionGridSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  // Links Column (spans 2 columns in Figma)
  linksColumnFigma: {
    flex: 2,
  },
  linksColumnFigmaSmall: {
    flex: 0,
    width: '100%',
  },
  linksCardContainer: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["4"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  linksCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  linksSectionGray: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
  },
  linksSectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  linksSectionTitleText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    width: '100%',
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
    lineHeight: 20,
  },
  // Comments Column - Unified with GoalDetailScreen
  commentsColumn: {
    flex: 1,
  },
  commentsColumnSmall: {
    flex: 0,
    width: '100%',
  },
  // KPI Cards Grid
  // Horizontal scroll container for cards
  horizontalScrollContainer: {
    flexGrow: 0,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingBottom: tokens.spacing.absolute["3"],
  },
  kpiValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  kpiValueBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: 100,
  },
  kpiValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
  },
  kpiProgressContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  kpiProgressTrack: {
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    position: 'relative',
    overflow: 'visible',
  },
  kpiProgressFill: {
    position: 'absolute',
    end: 0,
    top: 0,
    height: '100%',
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  kpiProgressDot: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: tokens.radii.absolute["radius-7"],
    top: -3,
    borderWidth: 2,
    borderColor: COLORS.white,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
    elevation: 2,
  },
  kpiDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    minHeight: 24,
  },
  kpiDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Activity Card
  activityCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    width: 288, // Fixed width (w-72)
    flexShrink: 0, // Prevent shrinking
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["4"],
    overflow: 'hidden',
  },
  activityCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  // Empty State
  emptyStateContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: tokens.spacing.absolute["6"],
    minWidth: '100%',
  },
  emptyStateText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.absolute["6"],
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    fontWeight: '600',
    color: tokens.colors.raw.gray["800"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  modalButtons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["4"],
  },
  rejectionReasonInput: {
    borderWidth: tokens.spacing.absolute["px"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    marginVertical: tokens.spacing.absolute["4"],
    minHeight: 100,
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    textAlignVertical: 'top',
  },
});

const styles = getStyles(true);
